/**
 * Configured axios instance for the REST API.
 *
 * Responsibilities:
 *  - Attach the JWT access token to every request.
 *  - Transparently refresh an expired access token on a 401 using the
 *    refresh token (single-flight: concurrent 401s share one refresh).
 *  - On an unrecoverable 401, clear the session and bounce to /login.
 *  - Normalize every rejection to `ApiError`.
 */
import axios from 'axios'
import { normalizeApiError } from './errors'
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import type { RefreshResponse } from '@/features/auth/types'
import { ROUTE_PATHS } from '@/config/app.config'
import { env } from '@/config/env'
import { useAuthStore } from '@/features/auth/stores/auth.store'
import { useImpersonationStore } from '@/features/auth/stores/impersonation.store'

const baseConfig = {
  baseURL: env.apiBaseUrl,
  timeout: env.apiTimeout,
  headers: { 'Content-Type': 'application/json' },
}

/** Main client — carries auth + refresh interceptors. */
export const apiClient: AxiosInstance = axios.create(baseConfig)

/** Bare client used only to refresh tokens (no interceptors → no recursion). */
const refreshClient: AxiosInstance = axios.create(baseConfig)

const REFRESH_ENDPOINT = '/auth/refresh'

/**
 * Public (pre-login) endpoints where a 401 means "bad credentials / invalid
 * token", not an expired session. A 401 here must never trigger a token
 * refresh or a forced logout — there is no session to recover, and doing so
 * would hard-redirect to /login and discard the error before the user can
 * read it. The rejection is surfaced to the caller (the sign-in form, the
 * invite-terms page) instead.
 */
const PUBLIC_AUTH_ENDPOINTS = ['/admin/auth/login', '/terms/invite', '/terms/accept']

// ── Request: inject bearer token ──────────────────────────────
apiClient.interceptors.request.use((config) => {
  // An explicit per-request Authorization header wins — e.g. the URL-hash
  // handoff validates a token that is not (yet) the ambient store token.
  if (config.headers.Authorization) return config
  // While a super admin is impersonating a tenant, the short-lived
  // impersonation token replaces the admin token so the backend scopes
  // every request to that tenant (see impersonation.store).
  const impersonationToken = useImpersonationStore.getState().session?.token
  const token = impersonationToken ?? useAuthStore.getState().accessToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ── Response: refresh-on-401 (single-flight) ──────────────────
type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean }

let refreshPromise: Promise<string> | null = null

/**
 * The session is unrecoverable (refresh failed or the backend still rejects a
 * fresh token): drop it and send the user to the login page.
 *
 * A hard redirect — rather than the TanStack router — keeps this module free
 * of an app-layer dependency and fully resets in-memory state on logout. The
 * pathname guard avoids a reload loop when the failing request *is* the login
 * attempt itself.
 */
function forceLogout(): void {
  useAuthStore.getState().clearSession()
  if (window.location.pathname !== ROUTE_PATHS.login) {
    window.location.replace(ROUTE_PATHS.login)
  }
}

/**
 * The impersonation token was rejected (expired or the session was revoked).
 * Drop the impersonation session and return to the platform (all-accounts)
 * view — but KEEP the super admin's own session so they aren't logged out.
 * A hard redirect resets in-memory query state scoped to the tenant.
 */
function exitImpersonationOnAuthFailure(): void {
  useImpersonationStore.getState().stop()
  if (window.location.pathname !== ROUTE_PATHS.accounts) {
    window.location.replace(ROUTE_PATHS.accounts)
  }
}

async function refreshAccessToken(): Promise<string> {
  const { refreshToken, setTokens, clearSession } = useAuthStore.getState()
  if (!refreshToken) {
    clearSession()
    throw new Error('No refresh token available')
  }
  try {
    const { data } = await refreshClient.post<RefreshResponse>(REFRESH_ENDPOINT, {
      refreshToken,
    })
    setTokens(data.tokens)
    return data.tokens.accessToken
  } catch (error) {
    clearSession()
    throw error
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined
    const status = error.response?.status
    const isRefreshCall = original?.url?.includes(REFRESH_ENDPOINT)
    const isPublicAuthCall = PUBLIC_AUTH_ENDPOINTS.some((path) => original?.url?.includes(path))

    // A 401 while impersonating means the impersonation token is dead. There
    // is no refresh for it — auto-exit to the platform view instead of tearing
    // down the whole (still-valid) super-admin session.
    if (status === 401 && useImpersonationStore.getState().session && !isPublicAuthCall) {
      exitImpersonationOnAuthFailure()
      return Promise.reject(normalizeApiError(error))
    }

    if (status === 401 && original && !original._retry && !isRefreshCall && !isPublicAuthCall) {
      original._retry = true
      try {
        // Concurrent 401s reuse the same in-flight refresh.
        refreshPromise = refreshPromise ?? refreshAccessToken()
        const newToken = await refreshPromise
        original.headers.Authorization = `Bearer ${newToken}`
        return apiClient(original)
      } catch (refreshError) {
        forceLogout()
        return Promise.reject(normalizeApiError(refreshError))
      } finally {
        refreshPromise = null
      }
    }

    // Any 401 reaching this point is unrecoverable (refresh disabled, the
    // retried request still came back 401, or the refresh call itself
    // rejected): the session is dead — log out and redirect to /login. A
    // failed sign-in attempt is excluded: it has no session to clear and the
    // error must reach the form rather than reload the page.
    if (status === 401 && !isPublicAuthCall) {
      forceLogout()
    }

    return Promise.reject(normalizeApiError(error))
  },
)

/**
 * Thin typed helpers that unwrap `response.data`. Prefer these in
 * service modules over calling `apiClient` directly.
 */
export const http = {
  get: <T>(url: string, config?: Parameters<AxiosInstance['get']>[1]) =>
    apiClient.get<T>(url, config).then((r) => r.data),
  post: <T>(url: string, body?: unknown, config?: Parameters<AxiosInstance['post']>[2]) =>
    apiClient.post<T>(url, body, config).then((r) => r.data),
  put: <T>(url: string, body?: unknown, config?: Parameters<AxiosInstance['put']>[2]) =>
    apiClient.put<T>(url, body, config).then((r) => r.data),
  patch: <T>(url: string, body?: unknown, config?: Parameters<AxiosInstance['patch']>[2]) =>
    apiClient.patch<T>(url, body, config).then((r) => r.data),
  delete: <T>(url: string, config?: Parameters<AxiosInstance['delete']>[1]) =>
    apiClient.delete<T>(url, config).then((r) => r.data),
}
